import { useState } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { useForecastSummary } from '@/hooks/use-projects';
import type { ProjectDetail } from '@hygge/shared';
import { ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { PlanVsActualCard } from './plan-vs-actual-card';

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(parseInt(year), parseInt(m) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function navigateMonth(month: string, delta: number): string {
  const [year, m] = month.split('-').map(Number);
  const date = new Date(year, m - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

interface ForecastTableProps {
  project: ProjectDetail;
}

// HW-732: the Monthly Forecast is now entered only in the Roster tab (single plan source).
// This Forecast view is read-only analytics: Plan vs Actual (planned side sourced from the
// Roster monthly forecast) plus the all-months Project Totals.
export function ForecastTable({ project }: ForecastTableProps) {
  const formatName = useEmployeeNameFormatter();
  const [month, setMonth] = useState(getCurrentMonth);
  const { data: summaryData } = useForecastSummary(project.id);

  return (
    <div className="space-y-6">
      {/* Month navigator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Month navigation, not a primary action: the site's neutral arrow
              button rather than the mint [HW-926] */}
          <Button size="icon" variant="outline" className="rounded-full" onClick={() => setMonth((m) => navigateMonth(m, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="min-w-[180px] text-center text-lg font-semibold">{formatMonth(month)}</h3>
          <Button size="icon" variant="outline" className="rounded-full" onClick={() => setMonth((m) => navigateMonth(m, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <FormulaHelp />
      </div>

      {/* Plan vs Actual — planned side sourced from the Roster monthly forecast (HW-732) */}
      <PlanVsActualCard projectId={project.id} month={month} />

      {/* Lifecycle summary */}
      {summaryData && summaryData.entries.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Project Totals (All Months)
            </h4>
            <Badge variant="outline" className="text-xs">
              Auto-calculated
            </Badge>
          </div>
          <div className="overflow-x-auto rounded-lg border-2 border-primary/20">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-primary/5">
                  <th className="px-4 py-3 text-left font-medium">Name</th>
                  <th className="px-3 py-3 text-right font-medium">Total Real Hrs</th>
                  <th className="px-3 py-3 text-right font-medium">Total Billable Hrs</th>
                  <th className="px-3 py-3 text-right font-medium">Total Cost</th>
                  <th className="px-3 py-3 text-right font-medium">Total Revenue</th>
                  <th className="px-3 py-3 text-right font-medium">Margin</th>
                </tr>
              </thead>
              <tbody>
                {summaryData.entries.map((emp) => (
                  <tr key={emp.employeeId} className="border-b last:border-b-0">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={emp.avatarUrl ?? undefined} />
                          <AvatarFallback className="text-xs">
                            {emp.firstName?.[0]}{emp.lastName?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{formatName({ firstName: emp.firstName, lastName: emp.lastName })}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{emp.totalRealHours.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right font-mono">{emp.totalBillableHours.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatCurrency(emp.totalMinExpected)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatCurrency(emp.totalMaxExpected)}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      <span className={emp.marginCoefficient >= 1 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                        {emp.marginCoefficient.toFixed(2)}x
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 bg-primary/5 font-bold">
                  <td className="px-4 py-3">Grand Total</td>
                  <td className="px-3 py-3 text-right font-mono">{summaryData.totals.realHours.toFixed(1)}</td>
                  <td className="px-3 py-3 text-right font-mono">{summaryData.totals.billableHours.toFixed(1)}</td>
                  <td className="px-3 py-3 text-right font-mono">{formatCurrency(summaryData.totals.minExpected)}</td>
                  <td className="px-3 py-3 text-right font-mono">{formatCurrency(summaryData.totals.maxExpected)}</td>
                  <td className="px-3 py-3 text-right font-mono">
                    <span className={summaryData.totals.marginCoefficient >= 1 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                      {summaryData.totals.marginCoefficient.toFixed(2)}x
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FormulaHelp() {
  return (
    <Popover>
      {/* A help trigger, not a primary action, so not the mint [HW-926] */}
      <PopoverTrigger asChild>
        <Button size="icon" variant="outline">
          <HelpCircle className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 text-sm">
        <h4 className="mb-3 font-semibold">How calculations work</h4>
        <div className="space-y-2 text-muted-foreground">
          <div>
            <span className="font-medium text-foreground">Min Expected (Cost)</span>
            <p>Real Hours &times; Nice-to-Have Rate</p>
          </div>
          <div>
            <span className="font-medium text-foreground">Max Expected (Revenue)</span>
            <p>Billable Hours &times; Real Rate</p>
          </div>
          <div>
            <span className="font-medium text-foreground">Margin Coefficient</span>
            <p>Max Expected &divide; Min Expected</p>
            <p className="text-xs">&ge; 1.0x means profitable (green), &lt; 1.0x means loss (red)</p>
          </div>
          <div>
            <span className="font-medium text-foreground">Variance %</span>
            <p>(Actual &minus; Planned) &divide; Planned &times; 100</p>
          </div>
          <div>
            <span className="font-medium text-foreground">Utilization</span>
            <p>Billable Hours &divide; Real Hours</p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
